/**
 * One PM's authoring session for one vendor: the catalog rows a draft is planned
 * against, the aliases and import profile that shape matching, the staged
 * review, and the commit that writes it.
 *
 * This is where Tasks 4-8 — pure functions all — meet a real fetch/mutate
 * boundary. Everything that decides anything still lives in those modules; this
 * hook only sequences them and owns the state they need. Two consequences worth
 * stating, because both look like details and are not:
 *
 * - The seed is built HERE, preserving each row's GraphQL `id`
 *   (`AuthoredSeed`, `costDraft.ts:49-63`), never via `seedFromRows`, which
 *   drops it (`rowsToSnapshot.ts:16-20`). `planWrites` reads that id into
 *   `previousId` and `buildCreateInputs` writes it as `supersedes`. Lose it
 *   and every row this feature has ever written claims to supersede nothing —
 *   silently, and permanently, because `supersedes` is immutable once written.
 *
 * - `planWrites` and `previewImpact` are both driven with NO `asOf`. They must
 *   agree on the basis they compute over, or the PM approves a ripple derived
 *   from a different catalog than the plan was. A client-side `now` is not
 *   safe as that basis: `recordedAt` is stamped by whichever author's browser
 *   wrote the row, so a concurrent author whose clock runs fast would have a
 *   real, already-visible row filtered straight back out of the review. The
 *   rows as fetched ARE the transaction time here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { fetchCatalogRows } from '../utils/catalogFetch';
import { createCatalogComponent } from '../graphql/catalogOps';
import {
  listComponentAliases, createComponentAlias,
  listCatalogImportProfiles, createCatalogImportProfile, updateCatalogImportProfile,
} from '../graphql/catalogAuthorOps';
import { componentSkusOf, productSkusOf } from '../catalog/catalogSkus';
import { matchDraft, normalizeRef } from '../catalog/matchDraft';
import { planWrites } from '../catalog/planWrites';
import { previewImpact } from '../catalog/previewImpact';
import { buildCreateInputs, detectStaleness } from '../catalog/commitRows';
import { publishQuarters } from '../pricing/publishQuarters';

const EMPTY_CATALOG = { components: [], compositions: [] };

/** Every write path in this hook takes the same lock, and every one of them
 *  refuses rather than queueing. See `busy` below. */
const IN_FLIGHT =
  'A commit is in flight. Wait for it to finish before changing the draft.';

/** Wire row -> AuthoredRow, KEEPING `id`. See the module comment. */
function toSeed(components, compositions) {
  return {
    rows: components.map((r) => ({
      id: r.id,
      vendor: r.vendor,
      effectiveFrom: r.effectiveFrom,
      componentSku: r.componentSku,
      kind: r.kind,
      value: r.value,
      recordedAt: r.recordedAt,
      version: r.version,
    })),
    compositions: compositions.map((r) => ({
      vendor: r.vendor,
      productSku: r.productSku,
      // AWSJSON on the wire, an array in the seed — same shape seedFromRows
      // parses, tolerant of an already-parsed value so a caller that hands in
      // fixtures does not have to double-encode them.
      components: typeof r.components === 'string' ? JSON.parse(r.components) : r.components,
      version: r.version,
    })),
  };
}

/** Wire profile -> ImportProfile with `mapping` ALREADY PARSED, mirroring
 *  `usePriceEditions.js`'s `toRecord`, so no page ever calls JSON.parse.
 *  A profile whose mapping is corrupt degrades to `mapping: null` — that costs
 *  the pre-fill, where throwing would cost the whole authoring page. */
function toProfile(item) {
  if (typeof item.mapping !== 'string') return { ...item, mapping: item.mapping ?? null };
  try {
    return { ...item, mapping: JSON.parse(item.mapping) };
  } catch (e) {
    console.error('Catalog import profile has an unreadable mapping:', e);
    return { ...item, mapping: null };
  }
}


/**
 * draft + aliases + catalog -> the review object, or a throw.
 *
 * Pure, and deliberately outside the component: `stage` and `resolveAlias` both
 * need it and must produce IDENTICAL reviews, which is easiest to guarantee when
 * there is one function and no closure for the two paths to disagree over.
 *
 * Throws only what `matchDraft` throws (a draft that writes one target twice in
 * one quarter). A failure to compute the IMPACT is not a throw — it comes back
 * as `impactError`, because the plan is still sound and still worth showing;
 * it is only the commit that must then be refused.
 */
function buildReview(draft, aliases, catalog, vendor) {
  const seed = toSeed(catalog.components, catalog.compositions);
  // `catalogSkus.ts` exists so classification-by-membership has ONE definition,
  // and both of Task 14's gates import it — but this hook re-inlined the two
  // expressions, so those gates were testing code production did not run. They
  // agree today, which is exactly the problem: the single most emphasised
  // invariant in this plan (a `VCH-FRU-*` part is a COMPONENT, decided by
  // membership and never by the shape of its SKU string) was pinned only on a
  // copy. Same seed the planner and the preview use, so a divergence between
  // what is classified and what is priced is not expressible either.
  const componentSkus = componentSkusOf(seed);
  const productSkus = productSkusOf(seed);

  const matched = matchDraft(draft, aliases, componentSkus, productSkus);
  const { rows, noOps, blocked } = planWrites(matched, seed);

  let impact = [];
  let impactError = null;
  try {
    // `publishQuarters(vendor)`, never a narrower window (decision 4): a ripple
    // approved over fewer quarters than publish actually uses is a ripple the
    // PM never saw.
    impact = previewImpact(rows, seed, publishQuarters(vendor));
  } catch (e) {
    console.error('Catalog impact preview failed:', e);
    impact = [];
    impactError = e instanceof Error ? e.message : String(e);
  }

  return {
    planned: rows,
    noOps,
    unmatched: matched.unmatched,
    blocked,
    impact,
    impactError,
  };
}

export function useCatalogDraft(vendor) {
  const [status, setStatus] = useState('loading');
  const [catalog, setCatalog] = useState(EMPTY_CATALOG);
  const [aliases, setAliases] = useState([]);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [review, setReview] = useState(null);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState([]);

  /** One lock over every write path, held from before the first await to the
   *  finally. A ref, not state: nothing renders it (pages own their own busy
   *  flag for the disabled attribute, as `EditionsPage.jsx:17` does), and it has
   *  to be readable synchronously by a callback that may have been created a
   *  render ago. `commit` re-fetches every target and re-checks staleness, so
   *  an alias or profile write inside that window re-matches the standing draft
   *  underneath a commit already in flight — the one race `detectStaleness`
   *  cannot see, because it compares catalog versions, not draft contents. */
  const busy = useRef(false);
  const fetchedFor = useRef(null);

  const load = useCallback(async () => {
    if (!vendor) return;
    const key = vendor;
    fetchedFor.current = key;
    try {
      const client = generateClient();
      const [rows, aliasRes, profileRes] = await Promise.all([
        fetchCatalogRows(vendor),
        client.graphql({ query: listComponentAliases, variables: { vendor } }),
        client.graphql({ query: listCatalogImportProfiles, variables: { vendor } }),
      ]);
      if (fetchedFor.current !== key) return;
      setCatalog({
        components: rows?.components ?? [],
        compositions: rows?.compositions ?? [],
      });
      // A partial alias list is worse than none: an unmatched ref whose alias
      // merely failed to load reads as "never mapped", and mapping it again
      // fails on attribute_not_exists(id). So a failure anywhere in this load is
      // the whole page's failure, not a degraded mode.
      setAliases(aliasRes?.data?.listComponentAliases?.items ?? []);
      const profiles = (profileRes?.data?.listCatalogImportProfiles?.items ?? []).map(toProfile);
      setProfile(profiles.find((p) => p.name === 'default') ?? profiles[0] ?? null);
      setStatus('ready');
    } catch (e) {
      console.error('Catalog authoring load failed:', e);
      if (fetchedFor.current !== key) return;
      setStatus('error');
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [vendor]);

  // Vendor change resets everything BEFORE the new load: a draft staged against
  // AIC's catalog must never survive into DELL's, where its refs mean something
  // else or nothing at all. Fires only when `load`'s identity changes.
  useEffect(() => {
    setStatus('loading');
    setCatalog(EMPTY_CATALOG);
    setAliases([]);
    setProfile(null);
    setDraft(null);
    setReview(null);
    setError(null);
    setConflicts([]);
    load();
  }, [load]);

  const stage = useCallback(async (nextDraft) => {
    if (busy.current) { setError(IN_FLIGHT); return null; }
    try {
      const next = buildReview(nextDraft, aliases, catalog, vendor);
      setDraft(nextDraft);
      setReview(next);
      setError(null);
      setConflicts([]);
      return next;
    } catch (e) {
      // matchDraft's duplicate-cell throw (Task 4, decision 3). A spreadsheet
      // with the same part twice in one quarter is malformed INPUT, not a cell
      // that failed to resolve, and half-importing it is worse than refusing
      // it — so nothing is staged and no partial review is left behind. The
      // message reaches the user intact: it names the ref and the quarter,
      // which is the only way they will find the offending row.
      console.error('Staging the draft failed:', e);
      setDraft(null);
      setReview(null);
      // Same clearing as reportUploadError's rejection path: a conflict list
      // describes a review, so it must not outlive one and render beside an
      // unrelated duplicate-cell error.
      setConflicts([]);
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, [aliases, catalog, vendor]);

  /**
   * Rebuild the STANDING review against current state, for the two places where
   * something changed underneath a draft the PM is still looking at: an alias
   * they just resolved, and rows a partial commit just wrote. One
   * implementation, deliberately — two copies of "re-match the standing draft"
   * would be two chances to rebuild it against a different catalog than the one
   * that just changed.
   *
   * Takes aliases and catalog as ARGUMENTS rather than reading state: both
   * callers have just computed a newer value than the render's, and `setState`
   * does not update the closure they are running in.
   *
   * Does NOT set `error` — it RETURNS the failure message and lets the caller
   * decide. `commit`'s partial path already has a more important message (how
   * many rows landed) which must not be clobbered by a re-match failure.
   * `review` is cleared on failure either way: a review that cannot be rebuilt
   * is describing a catalog that no longer exists, and must not stay on screen.
   */
  const rematch = useCallback((standingDraft, aliasList, catalogNow) => {
    if (!standingDraft) return null;
    try {
      setReview(buildReview(standingDraft, aliasList, catalogNow, vendor));
      return null;
    } catch (e) {
      console.error('Re-matching the standing draft failed:', e);
      setReview(null);
      return e instanceof Error ? e.message : String(e);
    }
  }, [vendor]);

  /**
   * The surface for a failure that happens BEFORE anything can be staged —
   * today, `readSheet` throwing on a duplicated ref or quarter column
   * (`readSheet.ts:49-53`). ImportPage reads the file inside a
   * `FileReader.onload` callback: a macrotask outside React's synchronous path,
   * where a throw is swallowed with no UI surface at all. A well-formed error
   * message nobody ever sees.
   *
   * Deliberately not a general error bus — it writes the one `error` field the
   * rest of this hook already reports through, and it clears the standing
   * review, because a review from the PREVIOUS file sitting next to a parse
   * failure still looks like it came from the file that just failed, and is
   * still committable.
   *
   * Pass null to clear — and null clears the review TOO, not just the error.
   * The null branch exists for exactly one caller's exactly one moment: the
   * top of `ImportPage`'s upload handler, synchronously, before the new
   * file's read even starts ("call it when a new upload starts", not "call it
   * to dismiss the message"). A file the PM is replacing may have gotten as
   * far as a staged, uncommitted review; if null left that review standing,
   * uploading file B would silently leave file A's review still committable
   * behind file B's fresh mapping form — the identical failure this function
   * exists to prevent for the FAILURE case, just reachable from the SUCCESS
   * case instead. There is currently no caller that wants to dismiss the
   * error while keeping the review — if one is ever added, it needs its own,
   * differently-named function, not a second meaning bolted onto this `null`.
   * (`discard` above is the other direction: drop the review on purpose, no
   * upload involved. The null branch delegates to it.)
   *
   * Not gated on `busy`: it issues no mutation and re-matches nothing, and
   * swallowing the message to protect a commit would reintroduce exactly the
   * failure this exists to fix.
   */
  /**
   * Drop the staged review without writing anything: the PM looked at it and
   * does not want it. The catalog, aliases and profile are untouched, so the
   * next `stage` plans against exactly what this one did. This is the
   * differently-named function `reportUploadError`'s doc comment reserved
   * for a caller that wants the review gone on its own terms — the Reset
   * button beside Commit (CONF-209) — and `reportUploadError(null)` now
   * delegates to it rather than keeping a second copy of the same clearing.
   *
   * Gated on `busy` like `stage`: a commit part-way through its sequential
   * writes must keep the review it is folding partial results back into.
   */
  const discard = useCallback(() => {
    if (busy.current) { setError(IN_FLIGHT); return; }
    setDraft(null);
    setReview(null);
    setConflicts([]);
    setError(null);
  }, []);

  const reportUploadError = useCallback((err) => {
    if (err === null || err === undefined) {
      discard();
      return;
    }
    console.error('Reading the uploaded sheet failed:', err);
    setDraft(null);
    setReview(null);
    setConflicts([]);
    setError(err instanceof Error ? err.message : String(err));
  }, [discard]);

  const resolveAlias = useCallback(async ({ ref, componentSku, actor } = {}) => {
    if (busy.current) { setError(IN_FLIGHT); return null; }
    const normalized = normalizeRef(ref ?? '');
    if (!normalized || !componentSku) {
      setError('An alias needs both a spreadsheet ref and a component SKU.');
      return null;
    }
    // matchDraft does not check that an alias's componentSku still exists, so a
    // bad one produces a matched cell for a missing SKU and a planned write
    // against nothing. Refusing to CREATE one is the cheap half of that fix.
    if (!catalog.components.some((r) => r.componentSku === componentSku)) {
      setError(`${componentSku} is not a component in the ${vendor} catalog.`);
      return null;
    }

    busy.current = true;
    try {
      const input = {
        // Deterministic, so two people mapping the same name concurrently
        // collide visibly on attribute_not_exists(id) instead of forking.
        id: `${vendor}#${normalized}`,
        vendor,
        ref: normalized,
        refDisplay: ref,
        componentSku,
        createdAt: new Date().toISOString(),
        ...(actor ? { createdBy: actor } : {}),
      };
      const client = generateClient();
      await client.graphql({ query: createComponentAlias, variables: { input } });

      // Learned, not merely persisted: the standing draft re-matches, and the
      // cell moves from unmatched to planned without a re-upload.
      const next = [...aliases, input];
      setAliases(next);
      setError(null);
      const failure = rematch(draft, next, catalog);
      if (failure) setError(failure);
      return input;
    } catch (e) {
      console.error('Writing the component alias failed:', e);
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      busy.current = false;
    }
  }, [aliases, catalog, draft, rematch, vendor]);

  const saveProfile = useCallback(async (mapping, { name = 'default', actor } = {}) => {
    if (busy.current) { setError(IN_FLIGHT); return null; }
    if (!mapping) { setError('There is no column mapping to save.'); return null; }

    busy.current = true;
    try {
      const existing = profile?.id ? profile : null;
      const input = {
        id: existing?.id ?? `${vendor}#${name}`,
        vendor,
        name: existing?.name ?? name,
        mapping: JSON.stringify(mapping),
        updatedAt: new Date().toISOString(),
        ...(actor ? { updatedBy: actor } : {}),
      };
      // There is no upsert — the caller picks. An update carries the same full
      // input, so the two branches differ only in the document.
      const client = generateClient();
      await client.graphql({
        query: existing ? updateCatalogImportProfile : createCatalogImportProfile,
        variables: { input },
      });
      const saved = toProfile(input);
      setProfile(saved);
      setError(null);
      return saved;
    } catch (e) {
      console.error('Saving the import profile failed:', e);
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      busy.current = false;
    }
  }, [profile, vendor]);

  /**
   * Refuses rather than returning null when it must not run — a null commit
   * forces every call site into a guard and leaves the panel's `disabled`
   * attribute as the only real protection. The load-bearing behaviour is that
   * zero `CreateCatalogComponent` mutations are issued.
   *
   * Never throws. Every outcome comes back as
   * `{ committed, written, total, reason }` so a page can act on it without a
   * try/catch, and the human-readable half is already in `error`/`conflicts`.
   */
  const commit = useCallback(async ({ actor } = {}) => {
    if (busy.current) {
      setError(IN_FLIGHT);
      return { committed: false, written: 0, total: 0, reason: 'busy' };
    }
    if (!review || review.planned.length === 0) {
      setError('There is nothing staged to commit.');
      return { committed: false, written: 0, total: 0, reason: 'nothing-staged' };
    }
    if (review.impactError) {
      // Spec §9. Approving a write whose ripple could not be computed defeats
      // the point of the review, so this is unavailable rather than discouraged.
      setError(
        `The price impact of this draft could not be computed, so it cannot be ` +
        `committed: ${review.impactError}`);
      return { committed: false, written: 0, total: 0, reason: 'impact-error' };
    }

    busy.current = true;
    try {
      setError(null);
      setConflicts([]);

      // 1. Re-resolve every target against the catalog as it is NOW, not as it
      //    was when the review was rendered.
      const current = await fetchCatalogRows(vendor);
      const stale = detectStaleness(review.planned, current?.components ?? []);

      // 2. Any movement aborts the WHOLE commit — not "everything except the
      //    conflicted row". The reviewed plan was computed against a catalog
      //    that no longer exists, and the rows that did not move were still
      //    priced against the ones that did. Nothing here is mutated: what the
      //    PM approved stays exactly as they approved it, and they re-import.
      if (stale.length > 0) {
        setConflicts(stale);
        setError(
          `${stale.length} ${stale.length === 1 ? 'row was' : 'rows were'} changed by ` +
          `someone else while this review was on screen ` +
          `(${stale.map((s) => `${s.componentSku} ${s.effectiveFrom}`).join(', ')}). ` +
          `Nothing was written. Re-run the import to review against the current catalog.`);
        return { committed: false, written: 0, total: 0, reason: 'stale', conflicts: stale };
      }

      // 3. Create-only inputs, versioned off the predecessor this plan resolved.
      const now = new Date().toISOString();
      const inputs = buildCreateInputs(review.planned, vendor, actor ?? 'unknown', now);
      const client = generateClient();

      // 4. Sequentially, counting successes. Sequential is the point: the count
      //    is only meaningful if the failure stops the run.
      let written = 0;
      try {
        for (const input of inputs) {
          // eslint-disable-next-line no-await-in-loop
          await client.graphql({ query: createCatalogComponent, variables: { input } });
          written += 1;
        }
      } catch (e) {
        // 5. Spec §8.3: a partial write is SAFE — prices do not move until
        //    publish, and re-running the import no-ops whatever landed (an
        //    already-written value is an unchanged value). What is not safe is
        //    leaving the PM to guess how much of it landed.
        console.error('Committing the catalog draft failed part-way:', e);
        // The rows that DID land, folded into the catalog exactly as the success
        // path folds in all of them — this is what makes the message below true.
        // Without it the hook's catalog still predates the PM's own partial
        // write, so re-running the import plans the landed row AGAIN off its old
        // version; commit then re-fetches, detectStaleness sees the version has
        // moved, and the whole commit aborts reporting the PM's own write as
        // "changed by someone else". There is no way out of that loop without
        // reloading the page. `written` is the count that succeeded and the loop
        // stops at the first failure, so slice(0, written) is exactly the set
        // that landed.
        const refreshed = {
          components: [...(current?.components ?? []), ...inputs.slice(0, written)],
          compositions: current?.compositions ?? [],
        };
        setCatalog(refreshed);
        // ...and the standing review rebuilt against THAT, not the pre-write
        // snapshot it was computed from. Rows WERE written here, so unlike the
        // stale-abort path above — where nothing was written and the review is
        // still accurate — leaving it alone would leave the PM reading a review
        // of a catalog that no longer exists: the landed row still listed as a
        // change, and a second commit click re-planning it off its old version
        // and aborting as though a phantom concurrent author had touched it.
        // Rebuilt, it shows the landed rows as no-ops and the failed ones still
        // planned: exactly what is left to do. Any re-match failure is returned,
        // not raised — the count below is the more important message.
        rematch(draft, aliases, refreshed);
        setError(
          `${written} of ${inputs.length} rows were written before the commit failed ` +
          `(${e instanceof Error ? e.message : String(e)}). Nothing is live until you ` +
          `publish, and the rows that landed are safe: re-run the import and it will ` +
          `report them as unchanged.`);
        return { committed: false, written, total: inputs.length, reason: 'partial' };
      }

      // The rows that just landed are the freshest thing anyone knows about this
      // catalog — they are the wire shape already — so the next draft plans
      // against them without a second round trip. The committed review is
      // cleared: left on screen it invites a second commit, which collides on
      // attribute_not_exists(id) and reports as a confusing partial failure.
      setCatalog({
        components: [...(current?.components ?? []), ...inputs],
        compositions: current?.compositions ?? [],
      });
      setDraft(null);
      setReview(null);
      return { committed: true, written, total: inputs.length };
    } catch (e) {
      console.error('Committing the catalog draft failed:', e);
      setError(e instanceof Error ? e.message : String(e));
      return { committed: false, written: 0, total: 0, reason: 'error' };
    } finally {
      busy.current = false;
    }
  }, [aliases, draft, rematch, review, vendor]);

  /** The normalised seed both admin views read: `usedByCount` counts
   *  compositions, and the grid resolves cells against `rows`. Memoized on
   *  `catalog` because `toSeed` JSON.parses every composition. */
  const seed = useMemo(
    () => toSeed(catalog.components, catalog.compositions),
    [catalog],
  );

  /**
   * The review a draft WOULD produce, without staging it — the live impact
   * panel's whole basis (SP3 spec §4).
   *
   * Deliberately the same `buildReview` that `stage` calls. Two implementations
   * of "what does this edit do" is exactly the divergence that let the shipped
   * editor show one thing while publish did another; there is one function and
   * no second code path.
   *
   * Synchronous, sets no state, stages nothing. A `matchDraft` throw (a draft
   * writing one target twice in one quarter) comes back as `impactError` rather
   * than propagating, because a live preview must never break the editor the
   * user is typing into.
   */
  const preview = useCallback((candidate) => {
    if (!candidate) return null;
    try {
      return buildReview(candidate, aliases, catalog, vendor);
    } catch (e) {
      return {
        planned: [], noOps: [], unmatched: [], blocked: [], impact: [],
        impactError: e instanceof Error ? e.message : String(e),
      };
    }
  }, [aliases, catalog, vendor]);

  return {
    status,
    aliases,
    profile,
    components: catalog.components,
    seed,
    stage,
    preview,
    review,
    commit,
    resolveAlias,
    saveProfile,
    reportUploadError,
    discard,
    error,
    conflicts,
  };
}
